import { css } from '@emotion/react';
import { Clear } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { capitalize } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { ContainerWithVerticalScrollShadow } from '../../../components/Form/FormComponents';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { SortableList } from '../../../components/SortableList/SortableList';
import { IField } from '../../../data-models/field2.data-model';
import { FieldDefinition } from '../../../data-models/field-definition/field-definition.schema';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { customFieldsByIdMapLoadable } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { kpiDefinitionByKeyMapAtom } from '../../../services/state/KPIConfigState';
import { cardStyles } from '../../../theme/component-styles';
import { ColumnMeta } from '../../../types';
import { columnsByPathStateLoadable } from '../state/ViewStateJ';
import { MprColumnConfig, MprColumnSortType } from './MultiPeriodReporting.schemas';
import {
  getColumnConfigKey,
  getFormattedConfigPeriod,
  isMprCustomFieldColumnConfig,
  isMprKpiColumnConfig,
  isMprMetricsColumnConfig,
} from './multiPeriodReportingUtils';
import { useMprColumnConfigs } from './useMprColumnConfigs';

const listHeight = '54dvh';
const formHeight = `calc(${listHeight} + 3rem)`;
const ListContainer = css`
  margin-top: 0.5rem;
  height: ${listHeight};
  max-height: ${listHeight};
`;
const ConfigCard = css({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 4rem 9rem auto',
  columnGap: '1rem',
  alignItems: 'center',
  ...cardStyles,
  padding: '0rem 1rem',
});

type ConfigWithId = MprColumnConfig & {
  id: string;
};

export function MprColumnConfigList() {
  const { setDraftColumnConfigs, draftColumnConfigs, setDraftColumnSortType, draftColumnSortType } =
    useMprColumnConfigs();
  const onDelete = useCallback(
    (config: ConfigWithId) => {
      setDraftColumnConfigs((prev) =>
        prev.filter((c) => getColumnConfigKey(c) !== getColumnConfigKey(config))
      );
    },
    [setDraftColumnConfigs]
  );

  const items: ConfigWithId[] = useMemo(
    () => draftColumnConfigs.map((c) => ({ ...c, id: getColumnConfigKey(c) })),
    [draftColumnConfigs]
  );
  const onChange = useCallback(
    (reorderedItems: ConfigWithId[]) => {
      setDraftColumnConfigs(() =>
        reorderedItems.map(({ id, ...config }, i) => ({ ...config, sortOrder: i }))
      );
    },
    [setDraftColumnConfigs]
  );

  const onSortTypeChange = useCallback(
    (type: MprColumnSortType | null) => {
      setDraftColumnSortType(type);
    },
    [setDraftColumnSortType]
  );

  if (draftColumnConfigs.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' textAlign={'center'} py={'3rem'} height={formHeight}>
        No columns selected
      </Typography>
    );
  }

  return (
    <Box minHeight={formHeight}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <ColumnSortTypeToggle value={draftColumnSortType} onChange={onSortTypeChange} />
        <Button color='secondary' onClick={() => onChange([])}>
          Remove All
        </Button>
      </Stack>
      <ContainerWithVerticalScrollShadow css={ListContainer}>
        <SortableList
          items={items}
          onChange={onChange}
          renderItem={(item) => (
            <SortableItem id={item.id}>
              <ColumnConfigListItem
                config={item}
                onDelete={onDelete}
                disableDrag={draftColumnSortType != null}
              />
            </SortableItem>
          )}
        />
      </ContainerWithVerticalScrollShadow>
    </Box>
  );
}

function ColumnConfigListItem({
  config,
  onDelete,
  disableDrag,
}: {
  config: ConfigWithId;
  onDelete: (config: ConfigWithId) => void;
  disableDrag?: boolean;
}) {
  const kpisByKey = useAtomValue(kpiDefinitionByKeyMapAtom);
  const columnsByPath = useAtomValue(columnsByPathStateLoadable(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  const customFieldsById = useAtomValue(customFieldsByIdMapLoadable);
  if (columnsByPath.state !== 'hasData' || customFieldsById.state !== 'hasData') {
    return null;
  }

  return (
    <>
      <Typography component='div' variant='body2' key={getColumnConfigKey(config)} css={ConfigCard}>
        <BasicDragHandle disabled={disableDrag} />
        <div>{getFieldDisplayName(config, kpisByKey, columnsByPath.data, customFieldsById.data)}</div>
        <div>{isMprKpiColumnConfig(config) ? capitalize(config.section) : ''}</div>
        <div>{getFormattedConfigPeriod(config)}</div>
        <IconButton onClick={() => onDelete(config)} sx={{ justifySelf: 'end' }}>
          <Clear fontSize='small' />
        </IconButton>
      </Typography>
    </>
  );
}

function getFieldDisplayName(
  config: ConfigWithId,
  kpisByKey: Map<string, FieldDefinition>,
  columnsByPath: Map<string, ColumnMeta>,
  customFieldsById: Map<number, IField<unknown>>
) {
  if (isMprKpiColumnConfig(config)) {
    return kpisByKey.get(config.kpiKey!)?.displayName;
  } else if (isMprMetricsColumnConfig(config)) {
    return columnsByPath.get(config.columnPath)?.displayName;
  } else if (isMprCustomFieldColumnConfig(config)) {
    return customFieldsById.get(config.customFieldId)?.displayName;
  }
}

interface IColumnSortTypeToggleProps {
  onChange: (type: MprColumnSortType | null) => void;
  value: MprColumnSortType | null;
}
function ColumnSortTypeToggle({ value, onChange }: IColumnSortTypeToggleProps) {
  return (
    <Stack direction={'row'} alignItems={'center'} gap={'0.5rem'}>
      <Typography variant={'body2'} color={'text.primary'} component={'label'}>
        Sort by
      </Typography>
      <ToggleButtonGroup
        id='column-sort-type'
        color='secondary'
        size='small'
        value={value ?? 'custom'}
        exclusive
        onChange={(e, newValue: MprColumnSortType | 'custom' | null) => {
          if (newValue == null) {
            return;
          }
          onChange(newValue == 'custom' ? null : newValue);
        }}
      >
        <ToggleButton value={MprColumnSortType.dateAsc}>Periodicity/date</ToggleButton>
        <ToggleButton value={MprColumnSortType.displayName}>Name</ToggleButton>
        <ToggleButton value={'custom'}>Custom</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
}
